import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, Loader2, Crown, CreditCard } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Payment icons
const ApplePayIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.08-.46-2.07-.48-3.2 0-1.42.62-2.16.44-3.02-.35C3.38 15.86 4.05 9.63 8.9 9.37c1.28.06 2.17.7 2.92.73.94-.19 1.84-.79 2.88-.74 1.22.08 2.15.58 2.75 1.48-2.53 1.51-1.93 4.84.55 5.77-.46 1.21-1.05 2.4-1.95 3.67zM12.3 9.3C12.08 7.09 13.86 5.28 16 5c.32 2.4-2.16 4.2-3.7 4.3z"/>
  </svg>
);

const GooglePayIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#4285F4" d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/>
  </svg>
);

const CashAppIcon = () => (
  <div className="w-5 h-5 rounded bg-[#00D632] flex items-center justify-center">
    <span className="text-white font-bold text-xs">$</span>
  </div>
);

const StripePayment = ({ 
  isOpen, 
  onClose, 
  onPaymentComplete 
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Pricing tiers
  const pricingPlans = [
    { id: 'monthly', label: 'Monthly', price: 9.99 },
    { id: 'yearly', label: 'Yearly', price: 99.99, badge: 'Save 17%' },
    { id: 'lifetime', label: 'Lifetime', price: 279.99, badge: 'Best Value' },
  ];

  // Check for payment success on return from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      setError('Payment verification timed out. Please contact support.');
      setCheckingStatus(false);
      return;
    }

    setCheckingStatus(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentSuccess(true);
        setCheckingStatus(false);
        localStorage.setItem('skybot_premium', 'true');
        setTimeout(() => {
          onPaymentComplete?.();
          onClose?.();
        }, 2000);
        return;
      } else if (response.data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setCheckingStatus(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  // Go DIRECTLY to Stripe checkout
  const goToStripeCheckout = async (planId) => {
    setLoading(true);
    setLoadingPlan(planId);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/checkout/create`, {
        package_id: planId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to connect. Please try again.');
      setLoading(false);
      setLoadingPlan(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(10px)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="w-full max-w-sm rounded-2xl overflow-hidden"
          style={{ background: '#fff', boxShadow: '0 25px 80px rgba(0,0,0,0.3)' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Payment Success */}
          {paymentSuccess ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center py-12 px-6"
            >
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-500/20">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="font-bold text-xl text-gray-900 mb-2">Payment Successful!</h3>
              <p className="text-gray-500">Premium features unlocked.</p>
            </motion.div>
          ) : checkingStatus ? (
            <div className="text-center py-12 px-6">
              <Loader2 className="w-8 h-8 text-blue-500 mx-auto mb-4 animate-spin" />
              <p className="text-gray-500">Verifying payment...</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="p-5 border-b border-gray-100 relative">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-bold text-lg text-gray-900">Get Premium</h2>
                    <p className="text-xs text-gray-500">Unlock all features</p>
                  </div>
                </div>
              </div>

              {/* Payment Methods Accepted */}
              <div className="px-5 pt-4 pb-2">
                <p className="text-xs text-gray-500 mb-2 text-center">Pay with</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100">
                    <CreditCard className="w-4 h-4 text-gray-700" />
                    <span className="text-xs font-medium text-gray-700">Card</span>
                  </div>
                  <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100">
                    <ApplePayIcon />
                    <span className="text-xs font-medium text-gray-700">Pay</span>
                  </div>
                  <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-100">
                    <GooglePayIcon />
                    <span className="text-xs font-medium text-gray-700">Pay</span>
                  </div>
                  <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#00D632]/10">
                    <CashAppIcon />
                    <span className="text-xs font-medium text-gray-700">Cash App</span>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 text-center mt-2">
                  Payment options vary by device
                </p>
              </div>

              {/* Error */}
              {error && (
                <div className="mx-5 mt-3 p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              {/* Plan Buttons - Each goes DIRECTLY to Stripe */}
              <div className="p-5 space-y-3">
                {pricingPlans.map((plan) => (
                  <motion.button
                    key={plan.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => goToStripeCheckout(plan.id)}
                    disabled={loading}
                    className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all flex items-center justify-between disabled:opacity-50"
                  >
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{plan.label}</span>
                        {plan.badge && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500 text-white">
                            {plan.badge}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-xl text-gray-900">${plan.price}</span>
                      {loadingPlan === plan.id && (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Footer */}
              <div className="px-5 pb-5">
                <p className="text-center text-xs text-gray-400">
                  🔒 Secure checkout via Stripe
                </p>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StripePayment;
